/**
 * The overview's perimeter, and the one place the "no default, ever" rule is argued.
 *
 * ── THE RULE, AND WHY THIS SCREEN IS NOT AN EXCEPTION TO IT ─────────────────
 *
 * `CLAUDE.md` §3.4 is absolute: *"No default, ever. Not 'the latest run', not 'the
 * current season'."* `src/features/inseason/scope.ts` gives the reason in G16's words —
 * a read whose perimeter is not stated is a read over the whole estate — and
 * `HomeScreen` declined to carry a measure at all on exactly that ground: *"a tile here
 * would have to invent one."*
 *
 * This module does not weaken that rule, and the distinction it turns on is worth
 * stating precisely, because a future reader will otherwise read
 * {@link resolveRunId} as the defaulting the rule forbids.
 *
 * **The rule protects against a perimeter nobody can see.** The failure it names is a
 * number on a screen whose scope is unstated — the planner cannot tell what was counted,
 * and cannot reproduce it later. That is why a chapter screen with no `runId` issues no
 * request: it would be answering a question nobody asked.
 *
 * **This screen's subject IS the latest cycle**, which is a different thing from a
 * screen that silently assumes one. Three properties keep it on the right side:
 *
 *   1  **It is named, not assumed.** The run strip renders the run's country, period,
 *      entry point and generation date before any measure below it. A planner reads the
 *      perimeter first and the numbers second.
 *   2  **It is written to the URL.** {@link RUN_ID_PARAM} is replaced into the address
 *      as soon as the directory resolves, so the view is a shareable link and the same
 *      link re-read tomorrow shows the same run rather than a newer one. That is the G9
 *      property the rule is really protecting: a number re-derivable from a named run.
 *   3  **It is a choice, not a fact.** The URL wins over the directory whenever it names
 *      a run, so a planner who picked run B is not returned to run A by a refresh.
 *
 * **What is still refused.** Nothing here invents a country, a period, a season or a
 * date. The exit position is dated and the platform publishes no directory of its dates
 * — see {@link EXIT_RUN_ID_PARAM} — so that panel has no equivalent of the run directory
 * and is left genuinely unscoped until a date is chosen.
 */

import { isCalendarDate } from '@/lib/dates';

/**
 * The in-season cycle run every markdown figure on this screen is bounded by. `?runId=…`.
 *
 * Deliberately the same spelling `src/features/inseason/scope.ts` uses, and deliberately
 * NOT imported from it. `no-restricted-imports` makes the shell importing a feature a
 * build failure (G11: the shell composes features, not the reverse), and a URL is a
 * shared surface anyway — a planner who drills from here into `/inseason/markdown` keeps
 * the run because both ends spell the parameter the same way, which is a property of the
 * URL contract rather than of a shared constant.
 */
export const RUN_ID_PARAM = 'runId';

/**
 * The consolidation run the end-of-life panel is read from. `?exitRunId=…`.
 *
 * **It was a date, `?exitAsOf=`, resolved from `GET /exit/consolidation/doors/latest`, and
 * that was the wrong perimeter.** A consolidation snapshot is dated by the stock it measured,
 * not by when it ran, so a later run on an earlier stock day left the older snapshot as "the
 * newest date": on 2026-09-25 this panel served an orphaned 09-06 position from a run that made
 * no decisions over the night's real 09-05 cycle. The platform now keys every row to the run
 * that wrote it and publishes a run directory (`GET /exit/consolidation/runs`, bata-platform
 * ADR 7019), newest RECORDED first, and the endpoint that answered with a date is retired.
 *
 * The three properties ADR 9003 asks of a resolved perimeter hold as they did: the run is
 * RESOLVED from the platform's directory (position runs only — the job that writes the rows
 * this panel sums), NAMED above every measure, and WRITTEN to the URL, where a run already
 * present always wins.
 */
export const EXIT_RUN_ID_PARAM = 'exitRunId';

/**
 * The consolidation DECISION run the exit lanes panel is read from. `?exitLaneRunId=…`.
 *
 * A different run from {@link EXIT_RUN_ID_PARAM}'s, and so a different parameter: the lanes are
 * written by `exit.consolidation_run` under its own run id, the door position by
 * `exit.consolidation_position` under another (bata-platform ADR 7019). One parameter for both
 * would read one run's lanes against another run's id. Resolved, named and written exactly as
 * the position run is — ADR 9003's three properties.
 *
 * Not `runId`, which is what `/exit/consolidation` calls the same run: on this screen `runId`
 * is already the in-season cycle's. The panel's action carries the run across under the
 * chapter's own spelling.
 */
export const EXIT_LANES_RUN_ID_PARAM = 'exitLaneRunId';

/*
 * `?season=` was the lost-sales tile's perimeter (ADR 9015). The tile left the Cockpit with
 * DEC-87 (package A, 2026-09-30) and the post-season page reads its own season, so the Cockpit no
 * longer resolves one.
 */

/**
 * The as-at date gate D2's evidence is read at. `?asOf=…` — the in-season chapter's spelling,
 * which `/inseason/cost-to-serve` reads gate D2 at.
 *
 * **When the URL names none, no date is guessed** — see {@link resolveD2Perimeter}. The tile
 * reads `GET /inseason/d2-evidence/latest` (bata-platform ADR 6030), each market's newest
 * assessment, and names each market's own `assessed_on` beside its verdict. The tile's way in carried
 * that date to `/inseason/rebalancing` until the owner ruling of 2026-11-02 sent it to the one move
 * run's report, which reads no date. Before that read existed the tile read D2 at the run on screen's generation date,
 * which was a guess at when the gate was last assessed (ADR 9015, amended).
 */
export const D2_AS_OF_PARAM = 'asOf';

/** What the address bar says this screen is looking at. */
export interface OverviewScope {
  /** The run named in the URL, or `null` — never a fallback. */
  readonly runId: string | null;
  /** The consolidation run named in the URL, or `null`. */
  readonly exitRunId: string | null;
  /** The consolidation decision run named in the URL, or `null`. */
  readonly exitLaneRunId: string | null;
  /** The gate-D2 as-at date named in the URL, or `null` — and `null` for anything not a calendar date. */
  readonly asOf: string | null;
}

/** Re-exported: the rule now lives in `src/lib/dates.ts`, shared with the exit chapter. */
export { isCalendarDate };

/**
 * Read the perimeter out of the search string the shell already parsed.
 *
 * Takes the parsed parameters rather than reaching for `window`, for the reason §3.4
 * gives as its fourth rule — "parse from a string, not from `window`" — which is what
 * makes this testable without a jsdom location.
 */
export function readOverviewScope(params: Readonly<Record<string, string>>): OverviewScope {
  const runId = params[RUN_ID_PARAM];
  const exitRunId = params[EXIT_RUN_ID_PARAM];
  const exitLaneRunId = params[EXIT_LANES_RUN_ID_PARAM];
  const asOf = params[D2_AS_OF_PARAM];
  return {
    runId: runId !== undefined && runId !== '' ? runId : null,
    exitRunId: exitRunId !== undefined && exitRunId.trim() !== '' ? exitRunId.trim() : null,
    exitLaneRunId: exitLaneRunId !== undefined && exitLaneRunId.trim() !== '' ? exitLaneRunId.trim() : null,
    // A malformed date would reach the platform as a 422; the URL can be checked for it here.
    asOf: asOf !== undefined && isCalendarDate(asOf.trim()) ? asOf.trim() : null,
  };
}

/**
 * Which gate-D2 read the tile makes: the dated read at the URL's `?asOf=` when the planner named a
 * date, else each market's newest assessment from the directory read — never a date inferred from
 * something else. `latest` is a directory, bounded by the caller's scope server-side (G3.1), so it
 * needs no run and no date.
 */
export type D2Perimeter =
  | { readonly kind: 'chosen'; readonly asOf: string }
  | { readonly kind: 'latest' };

export function resolveD2Perimeter(chosen: string | null): D2Perimeter {
  return chosen === null ? { kind: 'latest' } : { kind: 'chosen', asOf: chosen };
}

/**
 * Which run the screen is about: the planner's choice, else the newest the directory
 * published, else none.
 *
 * `GET /inseason/runs` returns newest first — `(recorded_at DESC, run_id)` since bata-platform
 * ADR 6034, the instant the runner claimed the stage — so "newest" is the platform's own
 * statement rather than a sort invented here. It was `(generated_at, …)`, and `generated_at` is
 * the monthly window's end, so every weekly tick of a month tied and the "newest" run was
 * whichever content digest sorted first (bata-ui#145).
 *
 * Returns `null` when the directory is empty, which is a true finding about the estate
 * and not a failure: `plan.inseason_run` holds no rows until a cycle stage has both an
 * identity to hang a decision off and a write path to put it through. The caller renders
 * that as what it is.
 */
export function resolveRunId(
  chosen: string | null,
  directory: readonly { readonly run_id: string }[],
): string | null {
  if (chosen !== null) return chosen;
  return directory[0]?.run_id ?? null;
}

/**
 * Which consolidation run an exit panel is about: the planner's choice, else the newest the
 * platform recorded, else none — with the stock date it measured when the directory states it.
 * Used for both exit directories, the position runs and the decision runs, which share the shape.
 *
 * `newest` is the first row of `GET /exit/consolidation/runs?job_name=exit.consolidation_position`,
 * which the platform orders by `recorded_at` descending: "newest" is its statement, and it is
 * the newest RUN, which "the newest date" was not. An empty directory returns `null`, a true
 * finding about the estate that the panel renders as itself.
 */
export function resolveExitRun(
  chosen: string | null,
  newest: { readonly run_id: string; readonly as_of: string } | undefined,
): { readonly runId: string; readonly asOf: string | null } | null {
  if (chosen !== null) return { runId: chosen, asOf: newest?.run_id === chosen ? newest.as_of : null };
  if (newest === undefined) return null;
  return { runId: newest.run_id, asOf: newest.as_of };
}

/**
 * Which run the markdown sections read their price actions from (F16).
 *
 * `?runId=` is the CYCLE run the page is about, and the directory it resolves from lists every
 * `inseason_run` — distribute, price and rebalance alike — with no field saying which is which.
 * Only a price run writes price actions, so a page resolved onto a distribute run said "this
 * cycle proposed no price changes" over an impact strip charting 7–27 m COP of recovery.
 *
 * The rule, in order (`resolvePriceRun` applies it to the directory's own rows — `price_actions`, the
 * count the run's decision summary calls `price_actions_total` — with no read of its own):
 *
 *   * the directory has not answered — `pending`; nothing is read for a run not yet known;
 *   * the read failed, the run is outside the directory page, or the run HAS a price step — the run itself;
 *   * the run has none and a newer-or-older run in the directory does — `moved`: the newest run
 *     with a price step, and the run it moved from, so the screen names both;
 *   * no run in the directory has one — the run itself, whose empty state then says so.
 *
 * Nothing is invented: `moved` names a run the platform's directory published, the screen
 * states it above the figures, and the cycle run in the URL is left exactly as it was.
 */
export type PriceRun =
  | { readonly kind: 'pending' }
  | { readonly kind: 'same'; readonly runId: string }
  | { readonly kind: 'moved'; readonly runId: string; readonly from: string };

/** The run a resolved {@link PriceRun} reads, or `null` while it is pending or there is none. */
export function priceRunIdOf(priceRun: PriceRun | null): string | null {
  return priceRun === null || priceRun.kind === 'pending' ? null : priceRun.runId;
}
